// eslint-disable-next-line no-restricted-imports -- @klear/time/boundary is the allowed UTC parse entry for strings
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import isDate from 'lodash/isDate'
import isFinite from 'lodash/isFinite'
import isNil from 'lodash/isNil'
import isNumber from 'lodash/isNumber'
import isPlainObject from 'lodash/isPlainObject'
import isString from 'lodash/isString'
import toNumber from 'lodash/toNumber'

import { Timestamp } from '@klear/time'

dayjs.extend(utc)

function assertFiniteMs(ms: number): number {
  if (!isNumber(ms) || !isFinite(ms)) {
    throw new Error(`Timestamp: expected finite epoch milliseconds, got ${ms}`)
  }
  return Math.trunc(toNumber(ms))
}

function coerceToEpochMs(input: unknown): number {
  if (isNil(input)) {
    throw new Error('Timestamp: expected non-null')
  }
  if (input instanceof Timestamp) {
    return input.epochMs
  }
  if (isNumber(input)) {
    return assertFiniteMs(input)
  }
  if (isString(input)) {
    const trimmed = input.trim()
    if (trimmed === '') {
      throw new Error('Timestamp: empty string')
    }
    const d = dayjs.utc(trimmed)
    if (!d.isValid()) {
      throw new Error(`Timestamp: invalid ISO string: ${input}`)
    }
    return d.valueOf()
  }
  if (isDate(input)) {
    return assertFiniteMs(input.getTime())
  }
  if (dayjs.isDayjs(input)) {
    if (!input.isValid()) {
      throw new Error('Timestamp: invalid Dayjs')
    }
    return assertFiniteMs(input.valueOf())
  }
  if (isPlainObject(input)) {
    const o = input as Record<string, unknown>
    if (isNumber(o.epochMs)) {
      return assertFiniteMs(o.epochMs)
    }
    if (isNumber(o.epochSeconds)) {
      return assertFiniteMs(o.epochSeconds * 1000)
    }
    if (isNumber(o.seconds)) {
      return assertFiniteMs(o.seconds * 1000)
    }
  }
  throw new Error('Timestamp: unsupported input')
}

export function parseTimestamp(input: unknown): Timestamp | undefined {
  try {
    return new Timestamp(coerceToEpochMs(input))
  } catch {
    return undefined
  }
}
